import {
  ChangeEvent,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  CAPTURE_EDITOR_COLORS,
  CaptureEditorTool,
  CaptureExport,
  EditorDocument,
  EditorPoint,
  EditorRect,
  EditorShape,
  ImageSize,
  TEXT_LINE_HEIGHT,
  blurBlockFor,
  clampPointToRect,
  drawScene,
  exportAnnotatedCapture,
  fontSizeFor,
  fullImageRect,
  intersectRects,
  rectFromPoints,
  strokeWidthFor,
  textFont,
} from "../../Helper/CaptureAnnotation";
import {
  prefersReducedMotion,
  setCaptureEditorImageRect,
} from "../../Helper/CaptureFlight";
import { CaptureStringKey, useCaptureText } from "../../Helper/CaptureStrings";
import {
  AlertIcon,
  ArrowIcon,
  BlurIcon,
  BoxIcon,
  CloseIcon,
  CropIcon,
  PenIcon,
  RedoIcon,
  ArrowUpIcon,
  RetakeIcon,
  TextIcon,
  UndoIcon,
} from "../CaptureIcons/CaptureIcons";
import "./CaptureEditor.scss";

// ── Seeding (stories, restoring a draft): shapes in NORMALIZED 0..1 units ──

type SeedShape =
  | { type: "pen"; points: EditorPoint[]; color?: string }
  | { type: "arrow"; from: EditorPoint; to: EditorPoint; color?: string }
  | { type: "rect"; rect: EditorRect; color?: string }
  | { type: "text"; at: EditorPoint; text: string; color?: string }
  | { type: "blur"; rect: EditorRect };

export interface CaptureEditorSeed {
  shapes?: SeedShape[];
  crop?: EditorRect;
  tool?: CaptureEditorTool;
  color?: string;
  textDraft?: { at: EditorPoint; value: string };
}

export interface CaptureEditorProps {
  /** data: URL of the screenshot the host captured. */
  imageSrc: string;
  /** false → a plain preview with Send / Retake / Cancel (no tools). */
  annotate: boolean;
  canRetake: boolean;
  /** Upload / completion in flight. */
  sending?: boolean;
  /** 0..1 upload progress while sending. */
  progress?: number;
  hasError?: boolean;
  onSend: (result: CaptureExport) => void;
  onRetake: () => void;
  onCancel: () => void;
  seed?: CaptureEditorSeed;
}

interface View {
  scale: number;
  x: number;
  y: number;
}

type Corner = "nw" | "ne" | "sw" | "se";

type Draft =
  | {
      kind: "shape";
      shape: EditorShape;
      anchor: EditorPoint;
      startScreen: EditorPoint;
    }
  | {
      kind: "crop";
      mode: "new" | "move" | "resize";
      corner?: Corner;
      rect: EditorRect;
      startRect: EditorRect;
      startImage: EditorPoint;
    }
  | { kind: "text-tap"; startScreen: EditorPoint; at: EditorPoint };

interface History {
  entries: EditorDocument[];
  index: number;
}

interface TextEdit {
  at: EditorPoint;
  value: string;
}

const TOOLS: { tool: CaptureEditorTool; label: CaptureStringKey; Icon: any }[] =
  [
    { tool: "pen", label: "captureEditorPen", Icon: PenIcon },
    { tool: "arrow", label: "captureEditorArrow", Icon: ArrowIcon },
    { tool: "rect", label: "captureEditorRect", Icon: BoxIcon },
    { tool: "text", label: "captureEditorText", Icon: TextIcon },
    { tool: "blur", label: "captureEditorBlur", Icon: BlurIcon },
    { tool: "crop", label: "captureEditorCrop", Icon: CropIcon },
  ];

const MAX_HISTORY = 100;
const MAX_ZOOM = 8;
const MIN_CROP_PX = 24;
const HANDLE_HIT_PX = 26;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const distance = (a: EditorPoint, b: EditorPoint) =>
  Math.hypot(a.x - b.x, a.y - b.y);

const midpoint = (a: EditorPoint, b: EditorPoint) => ({
  x: (a.x + b.x) / 2,
  y: (a.y + b.y) / 2,
});

const stagePadding = (stage: { width: number }) =>
  stage.width < 480 ? 16 : 32;

/** Corner radius of the screenshot on the stage, in screen pixels. */
const IMAGE_CORNER_RADIUS = 14;

const roundedRectPath = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) => {
  context.beginPath();
  if (r <= 0) {
    context.rect(x, y, w, h);
    return;
  }
  context.moveTo(x + r, y);
  context.arcTo(x + w, y, x + w, y + h, r);
  context.arcTo(x + w, y + h, x, y + h, r);
  context.arcTo(x, y + h, x, y, r);
  context.arcTo(x, y, x + w, y, r);
  context.closePath();
};

const fitView = (
  rect: EditorRect,
  stage: { width: number; height: number },
): View => {
  const pad = stagePadding(stage);
  const scale = Math.max(
    0.0001,
    Math.min(
      Math.max(1, stage.width - pad * 2) / Math.max(1, rect.w),
      Math.max(1, stage.height - pad * 2) / Math.max(1, rect.h),
    ),
  );
  return {
    scale,
    x: (stage.width - rect.w * scale) / 2 - rect.x * scale,
    y: (stage.height - rect.h * scale) / 2 - rect.y * scale,
  };
};

// Zoomed in, the focused rect may not drift away from the stage edges.
const clampView = (
  view: View,
  rect: EditorRect,
  stage: { width: number; height: number },
): View => {
  const pad = stagePadding(stage);
  const width = rect.w * view.scale;
  const height = rect.h * view.scale;
  const x =
    width <= stage.width - pad * 2
      ? (stage.width - width) / 2 - rect.x * view.scale
      : clamp(
          view.x,
          stage.width - pad - (rect.x + rect.w) * view.scale,
          pad - rect.x * view.scale,
        );
  const y =
    height <= stage.height - pad * 2
      ? (stage.height - height) / 2 - rect.y * view.scale
      : clamp(
          view.y,
          stage.height - pad - (rect.y + rect.h) * view.scale,
          pad - rect.y * view.scale,
        );
  return { scale: view.scale, x, y };
};

const toImagePoint = (point: EditorPoint, view: View): EditorPoint => ({
  x: (point.x - view.x) / view.scale,
  y: (point.y - view.y) / view.scale,
});

const toScreenRect = (rect: EditorRect, view: View): EditorRect => ({
  x: rect.x * view.scale + view.x,
  y: rect.y * view.scale + view.y,
  w: rect.w * view.scale,
  h: rect.h * view.scale,
});

const hitCorner = (
  point: EditorPoint,
  rect: EditorRect,
): Corner | undefined => {
  const corners: [Corner, EditorPoint][] = [
    ["nw", { x: rect.x, y: rect.y }],
    ["ne", { x: rect.x + rect.w, y: rect.y }],
    ["sw", { x: rect.x, y: rect.y + rect.h }],
    ["se", { x: rect.x + rect.w, y: rect.y + rect.h }],
  ];
  const hit = corners.find(
    ([, corner]) => distance(point, corner) <= HANDLE_HIT_PX,
  );
  return hit?.[0];
};

const isInside = (point: EditorPoint, rect: EditorRect) =>
  point.x >= rect.x &&
  point.x <= rect.x + rect.w &&
  point.y >= rect.y &&
  point.y <= rect.y + rect.h;

const seedDocument = (
  seed: CaptureEditorSeed | undefined,
  size: ImageSize,
  fallbackColor: string,
): EditorDocument => {
  const px = (point: EditorPoint) => ({
    x: point.x * size.width,
    y: point.y * size.height,
  });
  const rect = (value: EditorRect) => ({
    x: value.x * size.width,
    y: value.y * size.height,
    w: value.w * size.width,
    h: value.h * size.height,
  });
  const width = strokeWidthFor(size);
  const toShape = (shape: SeedShape): EditorShape => {
    const shapeColor =
      "color" in shape && shape.color ? shape.color : fallbackColor;
    if (shape.type === "pen") {
      return {
        type: "pen",
        color: shapeColor,
        width,
        points: shape.points.map(px),
      };
    }
    if (shape.type === "arrow") {
      return {
        type: "arrow",
        color: shapeColor,
        width,
        from: px(shape.from),
        to: px(shape.to),
      };
    }
    if (shape.type === "rect") {
      return { type: "rect", color: shapeColor, width, rect: rect(shape.rect) };
    }
    if (shape.type === "text") {
      return {
        type: "text",
        color: shapeColor,
        fontSize: fontSizeFor(size),
        at: px(shape.at),
        text: shape.text,
      };
    }
    return { type: "blur", block: blurBlockFor(size), rect: rect(shape.rect) };
  };
  const shapes = (seed?.shapes ?? []).map(toShape);
  return {
    shapes,
    crop: seed?.crop ? rect(seed.crop) : fullImageRect(size),
  };
};

/**
 * Full-screen annotation editor for a screenshot the host SDK captured
 * (contract §7 `capture-image`). Draw / arrow / box / text / blur / crop with
 * undo + redo, mouse, touch and pen (pointer events) with pinch- and
 * wheel-zoom; exports a JPEG (long edge ≤ 2560, blur regions pixelated into
 * the pixels) plus the normalized annotation list.
 */
export const CaptureEditor = ({
  imageSrc,
  annotate,
  canRetake,
  sending = false,
  progress,
  hasError = false,
  onSend,
  onRetake,
  onCancel,
  seed,
}: CaptureEditorProps) => {
  const t = useCaptureText();
  const rootRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const [tool, setTool] = useState<CaptureEditorTool>(seed?.tool ?? "pen");
  // The tool Esc returns to when it leaves crop mode.
  const lastMarkToolRef = useRef<CaptureEditorTool>(
    seed?.tool && seed.tool !== "crop" ? seed.tool : "pen",
  );
  if (tool !== "crop") {
    lastMarkToolRef.current = tool;
  }
  const [color, setColor] = useState<string>(
    seed?.color ?? CAPTURE_EDITOR_COLORS[0].value,
  );
  const [history, setHistory] = useState<History | null>(null);
  const [textEdit, setTextEditState] = useState<TextEdit | null>(null);
  // Mirrored synchronously so a commit can never run twice (Enter, then the
  // blur of the input being removed) before React re-renders.
  const textEditRef = useRef<TextEdit | null>(null);
  const setTextEdit = (next: TextEdit | null) => {
    textEditRef.current = next;
    setTextEditState(next);
  };
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  // Layout follows the editor's OWN box (the widget frame, or the whole
  // viewport once the web host enlarges it) — measured, not media-queried,
  // so it also holds in WebViews without container queries (iOS 15).
  const [layout, setLayout] = useState({
    wide: false,
    short: false,
    compact: false,
    stacked: false,
  });
  const [exporting, setExporting] = useState(false);
  const [colorsOpen, setColorsOpen] = useState(false);
  const colorsOpenRef = useRef(false);
  colorsOpenRef.current = colorsOpen;
  const [exportFailed, setExportFailed] = useState(false);

  const size: ImageSize | undefined = image
    ? { width: image.naturalWidth, height: image.naturalHeight }
    : undefined;
  const doc = history ? history.entries[history.index] : undefined;
  const isBusy = sending || exporting;
  // Leaving with markup (Esc or ×) asks first: the drawings would be gone.
  const hasMarkup = !!history && history.index > 0;
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const requestCancel = () => {
    if (hasMarkup && !confirmDiscard) {
      setConfirmDiscard(true);
      return;
    }
    onCancel();
  };
  const cropMode = annotate && tool === "crop";

  const viewRef = useRef<View | null>(null);
  const draftRef = useRef<Draft | null>(null);
  const pointersRef = useRef(new Map<number, EditorPoint>());
  const pinchRef = useRef<{
    startDistance: number;
    startMid: EditorPoint;
    startView: View;
  } | null>(null);
  const frameRef = useRef<number | null>(null);

  // Latest render values for the imperative (pointer / wheel / key) handlers.
  const latest = useRef({
    image,
    size,
    doc,
    tool,
    color,
    textEdit,
    stageSize,
    annotate,
    cropMode,
    isBusy,
    onCancel: requestCancel,
    confirmDiscard,
  });
  latest.current = {
    image,
    size,
    doc,
    tool,
    color,
    textEdit,
    stageSize,
    annotate,
    cropMode,
    isBusy,
    onCancel: requestCancel,
    confirmDiscard,
  };

  // ── Image ──
  useEffect(() => {
    let disposed = false;
    const element = new Image();
    element.onload = () => {
      if (disposed) {
        return;
      }
      const loadedSize = {
        width: element.naturalWidth,
        height: element.naturalHeight,
      };
      setImage(element);
      setHistory({
        entries: [
          seedDocument(
            seed,
            loadedSize,
            seed?.color ?? CAPTURE_EDITOR_COLORS[0].value,
          ),
        ],
        index: 0,
      });
      if (seed?.textDraft) {
        setTextEdit({
          at: {
            x: seed.textDraft.at.x * loadedSize.width,
            y: seed.textDraft.at.y * loadedSize.height,
          },
          value: seed.textDraft.value,
        });
      }
    };
    element.onerror = () => {
      if (!disposed) {
        setImageFailed(true);
      }
    };
    element.src = imageSrc;
    return () => {
      disposed = true;
    };
    // The seed only applies to the first image of this editor instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSrc]);

  // ── Editor + stage size ──
  // Width the toolbar needs with its labels (measured once it overflowed);
  // narrower frames get the icon-only bar instead of a clipped Send.
  const barRef = useRef<HTMLDivElement>(null);
  const labelledBarWidthRef = useRef(0);
  const updateLayoutRef = useRef<() => void>(() => {});
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }
    const update = () => {
      const width = root.clientWidth;
      const height = root.clientHeight;
      const next = {
        wide: width >= Math.max(640, labelledBarWidthRef.current),
        short: height <= 500 && width > height,
        compact: width < 420,
        // Six tools, colour, history, Retake and Send don't fit one row.
        stacked: width < 560,
      };
      setLayout((current) =>
        current.wide === next.wide &&
        current.short === next.short &&
        current.compact === next.compact &&
        current.stacked === next.stacked
          ? current
          : next,
      );
    };
    updateLayoutRef.current = update;
    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  // The labelled bar is ~800px in English and longer in other languages: when
  // it overflows its frame, remember what it needs and drop the labels.
  useLayoutEffect(() => {
    const bar = barRef.current;
    const toolbar = bar?.parentElement;
    if (!layout.wide || !bar || !toolbar) {
      return;
    }
    const style = window.getComputedStyle(toolbar);
    const room =
      toolbar.clientWidth -
      (parseFloat(style.paddingLeft) || 0) -
      (parseFloat(style.paddingRight) || 0);
    if (bar.scrollWidth > room + 1) {
      labelledBarWidthRef.current =
        bar.scrollWidth + (toolbar.clientWidth - room);
      updateLayoutRef.current();
    }
  });

  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) {
      return;
    }
    const update = () =>
      setStageSize((current) =>
        current.width === stage.clientWidth &&
        current.height === stage.clientHeight
          ? current
          : { width: stage.clientWidth, height: stage.clientHeight },
      );
    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  const focusRect = (): EditorRect | undefined => {
    const {
      size: currentSize,
      doc: currentDoc,
      cropMode: inCropMode,
    } = latest.current;
    if (!currentSize || !currentDoc) {
      return undefined;
    }
    return inCropMode ? fullImageRect(currentSize) : currentDoc.crop;
  };

  // ── Drawing ──
  const positionTextInput = (view: View) => {
    const input = textInputRef.current;
    const edit = textEditRef.current;
    const currentSize = latest.current.size;
    if (!input || !edit || !currentSize) {
      return;
    }
    const screenFont = fontSizeFor(currentSize) * view.scale;
    // The input is typed at 16px (no iOS focus zoom) and scaled to the size
    // the text will be drawn at, so what you type is where it will be drawn.
    input.style.left = `${edit.at.x * view.scale + view.x}px`;
    input.style.top = `${edit.at.y * view.scale + view.y}px`;
    input.style.transform = `scale(${screenFont / 16})`;
    input.style.visibility = "visible";
  };

  const draw = () => {
    frameRef.current = null;
    const canvas = canvasRef.current;
    const {
      image: currentImage,
      size: currentSize,
      doc: currentDoc,
      stageSize: stage,
      cropMode: inCropMode,
    } = latest.current;
    if (!canvas || stage.width === 0 || stage.height === 0) {
      return;
    }
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    const pixelWidth = Math.round(stage.width * dpr);
    const pixelHeight = Math.round(stage.height * dpr);
    if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
    }
    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, pixelWidth, pixelHeight);
    if (!currentImage || !currentSize || !currentDoc) {
      return;
    }
    const rect = focusRect() ?? fullImageRect(currentSize);
    const view = viewRef.current ?? fitView(rect, stage);
    viewRef.current = view;

    const draft = draftRef.current;
    const shownCrop = draft?.kind === "crop" ? draft.rect : currentDoc.crop;
    const visible = inCropMode ? fullImageRect(currentSize) : currentDoc.crop;

    context.setTransform(
      dpr * view.scale,
      0,
      0,
      dpr * view.scale,
      dpr * view.x,
      dpr * view.y,
    );

    // The screenshot sits on the stage like a card: rounded corners and a
    // layered shadow (display only, the exported image keeps its corners).
    const radius = inCropMode
      ? 0
      : Math.min(
          IMAGE_CORNER_RADIUS / view.scale,
          visible.w / 2,
          visible.h / 2,
        );
    context.save();
    context.fillStyle = "#ffffff";
    context.shadowColor = "rgba(0, 0, 0, 0.16)";
    context.shadowBlur = 32 * dpr;
    context.shadowOffsetY = 12 * dpr;
    roundedRectPath(
      context,
      visible.x,
      visible.y,
      visible.w,
      visible.h,
      radius,
    );
    context.fill();
    context.shadowColor = "rgba(0, 0, 0, 0.12)";
    context.shadowBlur = 3 * dpr;
    context.shadowOffsetY = 1 * dpr;
    context.fill();
    context.restore();

    context.save();
    if (!inCropMode) {
      roundedRectPath(
        context,
        visible.x,
        visible.y,
        visible.w,
        visible.h,
        radius,
      );
      context.clip();
    }
    const shapes =
      draft?.kind === "shape"
        ? [...currentDoc.shapes, draft.shape]
        : currentDoc.shapes;
    drawScene(context, currentImage, currentSize, shapes);
    context.restore();

    if (inCropMode) {
      const full = toScreenRect(fullImageRect(currentSize), view);
      const crop = toScreenRect(shownCrop, view);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      context.fillStyle = "rgba(0, 0, 0, 0.5)";
      context.fillRect(full.x, full.y, full.w, Math.max(0, crop.y - full.y));
      context.fillRect(
        full.x,
        crop.y + crop.h,
        full.w,
        Math.max(0, full.y + full.h - (crop.y + crop.h)),
      );
      context.fillRect(full.x, crop.y, Math.max(0, crop.x - full.x), crop.h);
      context.fillRect(
        crop.x + crop.w,
        crop.y,
        Math.max(0, full.x + full.w - (crop.x + crop.w)),
        crop.h,
      );
      // White lines with a dark halo: the frame stays visible on light
      // screenshots too, where the dimmed outside alone is not enough (an
      // uncropped image has no dimmed outside at all).
      context.save();
      context.shadowColor = "rgba(0, 0, 0, 0.3)";
      context.shadowBlur = 1.5 * dpr;
      context.strokeStyle = "rgba(255, 255, 255, 0.6)";
      context.lineWidth = 1;
      context.beginPath();
      [1, 2].forEach((third) => {
        context.moveTo(crop.x + (crop.w * third) / 3, crop.y);
        context.lineTo(crop.x + (crop.w * third) / 3, crop.y + crop.h);
        context.moveTo(crop.x, crop.y + (crop.h * third) / 3);
        context.lineTo(crop.x + crop.w, crop.y + (crop.h * third) / 3);
      });
      context.stroke();
      context.shadowColor = "rgba(0, 0, 0, 0.55)";
      context.shadowBlur = 3 * dpr;
      context.strokeStyle = "#ffffff";
      context.lineWidth = 2;
      context.strokeRect(crop.x, crop.y, crop.w, crop.h);
      const arm = Math.min(20, crop.w / 2, crop.h / 2);
      context.lineWidth = 4;
      context.lineCap = "round";
      context.beginPath();
      (
        [
          [crop.x, crop.y, 1, 1],
          [crop.x + crop.w, crop.y, -1, 1],
          [crop.x, crop.y + crop.h, 1, -1],
          [crop.x + crop.w, crop.y + crop.h, -1, -1],
        ] as [number, number, number, number][]
      ).forEach(([x, y, dx, dy]) => {
        context.moveTo(x + dx * arm, y);
        context.lineTo(x, y);
        context.lineTo(x, y + dy * arm);
      });
      context.stroke();
      context.restore();
    }

    positionTextInput(view);

    // Where the screenshot sits (the flight into the card starts here).
    const box = canvas.getBoundingClientRect();
    const onScreen = toScreenRect(visible, view);
    setCaptureEditorImageRect({
      x: box.left + onScreen.x,
      y: box.top + onScreen.y,
      width: onScreen.w,
      height: onScreen.h,
    });
    // The web host enlarges the frame right after the editor mounts: until the
    // stage size caught up with the canvas' real box, this frame was fitted
    // for the old size and a grow-in from it would start zoomed in and off
    // centre. The resize redraws and grows in then.
    const stageCurrent =
      Math.abs(canvas.clientWidth - stage.width) <= 1 &&
      Math.abs(canvas.clientHeight - stage.height) <= 1;
    if (!grewIn.current && stageCurrent) {
      grewIn.current = true;
      growIn(canvas, box, onScreen);
    }
  };

  // The page shrinks into the editor: the first frame starts at the size the
  // screenshot had as the page itself (the viewport) and settles into place.
  const grewIn = useRef(false);
  const growIn = (
    canvas: HTMLCanvasElement,
    box: DOMRect,
    onScreen: EditorRect,
  ) => {
    if (
      prefersReducedMotion() ||
      typeof canvas.animate !== "function" ||
      onScreen.w <= 0 ||
      onScreen.h <= 0
    ) {
      return;
    }
    const viewportWidth = window.innerWidth || box.width;
    const viewportHeight = window.innerHeight || box.height;
    const scale = Math.max(
      1,
      Math.min(viewportWidth / onScreen.w, viewportHeight / onScreen.h),
    );
    if (scale < 1.05) {
      return;
    }
    const centerX = box.left + onScreen.x + onScreen.w / 2;
    const centerY = box.top + onScreen.y + onScreen.h / 2;
    const dx = viewportWidth / 2 - centerX;
    const dy = viewportHeight / 2 - centerY;
    canvas.style.transformOrigin = `${onScreen.x + onScreen.w / 2}px ${
      onScreen.y + onScreen.h / 2
    }px`;
    try {
      canvas.animate(
        [
          { transform: `translate(${dx}px, ${dy}px) scale(${scale})` },
          { transform: "none" },
        ],
        { duration: 460, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" },
      );
    } catch (_) {}
  };

  useEffect(() => () => setCaptureEditorImageRect(undefined), []);

  const scheduleDraw = () => {
    if (frameRef.current !== null) {
      return;
    }
    frameRef.current = window.requestAnimationFrame(draw);
  };

  useEffect(() => {
    return () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  // Refit whenever what we look at changes: new image, resized stage (rotation,
  // the web host enlarging the frame), a committed crop, entering crop mode.
  const cropKey = doc
    ? `${doc.crop.x}:${doc.crop.y}:${doc.crop.w}:${doc.crop.h}`
    : "";
  useEffect(() => {
    const rect = focusRect();
    if (rect && stageSize.width > 0 && stageSize.height > 0) {
      viewRef.current = fitView(rect, stageSize);
    }
    scheduleDraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image, stageSize.width, stageSize.height, cropKey, cropMode]);

  // Anything else that changes the picture.
  useEffect(() => {
    scheduleDraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, textEdit, color]);

  // ── History ──
  const pushDocument = (next: EditorDocument) => {
    setHistory((current) => {
      if (!current) {
        return current;
      }
      const entries = [
        ...current.entries.slice(0, current.index + 1),
        next,
      ].slice(-MAX_HISTORY);
      return { entries, index: entries.length - 1 };
    });
  };

  const canUndo = !!history && history.index > 0;
  const canRedo = !!history && history.index < history.entries.length - 1;

  const undo = () => {
    setTextEdit(null);
    setHistory((current) =>
      current && current.index > 0
        ? { ...current, index: current.index - 1 }
        : current,
    );
  };

  const redo = () => {
    setTextEdit(null);
    setHistory((current) =>
      current && current.index < current.entries.length - 1
        ? { ...current, index: current.index + 1 }
        : current,
    );
  };

  const textShapeFor = (edit: TextEdit) => {
    const currentSize = latest.current.size;
    const text = edit.value.trim();
    if (!currentSize || text.length === 0) {
      return undefined;
    }
    return {
      type: "text" as const,
      color: latest.current.color,
      fontSize: fontSizeFor(currentSize),
      at: edit.at,
      text,
    };
  };

  const commitText = () => {
    const edit = textEditRef.current;
    if (!edit) {
      return;
    }
    const shape = textShapeFor(edit);
    setTextEdit(null);
    if (!shape) {
      return;
    }
    setHistory((current) => {
      if (!current) {
        return current;
      }
      const base = current.entries[current.index];
      const entries = [
        ...current.entries.slice(0, current.index + 1),
        { ...base, shapes: [...base.shapes, shape] },
      ].slice(-MAX_HISTORY);
      return { entries, index: entries.length - 1 };
    });
  };

  const selectTool = (next: CaptureEditorTool) => {
    setColorsOpen(false);
    commitText();
    setTool(next);
  };

  // ── Pointer input ──
  const stagePoint = (event: { clientX: number; clientY: number }) => {
    const box = stageRef.current?.getBoundingClientRect();
    return {
      x: event.clientX - (box?.left ?? 0),
      y: event.clientY - (box?.top ?? 0),
    };
  };

  const startPinch = () => {
    const points = Array.from(pointersRef.current.values());
    const view = viewRef.current;
    if (points.length < 2 || !view) {
      return;
    }
    draftRef.current = null;
    pinchRef.current = {
      startDistance: Math.max(1, distance(points[0], points[1])),
      startMid: midpoint(points[0], points[1]),
      startView: view,
    };
  };

  const updatePinch = () => {
    const pinch = pinchRef.current;
    const points = Array.from(pointersRef.current.values());
    const rect = focusRect();
    const stage = latest.current.stageSize;
    if (!pinch || points.length < 2 || !rect) {
      return;
    }
    const fit = fitView(rect, stage);
    const mid = midpoint(points[0], points[1]);
    const scale = clamp(
      (pinch.startView.scale * distance(points[0], points[1])) /
        pinch.startDistance,
      fit.scale,
      fit.scale * MAX_ZOOM,
    );
    const anchor = toImagePoint(pinch.startMid, pinch.startView);
    viewRef.current =
      scale <= fit.scale * 1.01
        ? fit
        : clampView(
            { scale, x: mid.x - anchor.x * scale, y: mid.y - anchor.y * scale },
            rect,
            stage,
          );
    scheduleDraw();
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const {
      image: currentImage,
      size: currentSize,
      doc: currentDoc,
      isBusy: busy,
      annotate: canAnnotate,
    } = latest.current;
    if (!currentImage || !currentSize || !currentDoc || busy) {
      return;
    }
    if (colorsOpenRef.current) {
      setColorsOpen(false);
    }
    if (textEditRef.current) {
      // A tap outside the text being typed finishes it (and does no more).
      commitText();
      return;
    }
    event.preventDefault();
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch (_) {}
    const screen = stagePoint(event);
    pointersRef.current.set(event.pointerId, screen);
    if (pointersRef.current.size === 2) {
      startPinch();
      return;
    }
    if (pointersRef.current.size > 2 || !canAnnotate) {
      return;
    }
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }
    const view = viewRef.current;
    if (!view) {
      return;
    }
    const imagePoint = toImagePoint(screen, view);
    const full = fullImageRect(currentSize);
    const bounds = latest.current.cropMode ? full : currentDoc.crop;
    const point = clampPointToRect(imagePoint, bounds);
    const currentColor = latest.current.color;
    const width = strokeWidthFor(currentSize);

    switch (latest.current.tool) {
      case "pen":
        draftRef.current = {
          kind: "shape",
          anchor: point,
          startScreen: screen,
          shape: { type: "pen", color: currentColor, width, points: [point] },
        };
        break;
      case "arrow":
        draftRef.current = {
          kind: "shape",
          anchor: point,
          startScreen: screen,
          shape: {
            type: "arrow",
            color: currentColor,
            width,
            from: point,
            to: point,
          },
        };
        break;
      case "rect":
        draftRef.current = {
          kind: "shape",
          anchor: point,
          startScreen: screen,
          shape: {
            type: "rect",
            color: currentColor,
            width,
            rect: { x: point.x, y: point.y, w: 0, h: 0 },
          },
        };
        break;
      case "blur":
        draftRef.current = {
          kind: "shape",
          anchor: point,
          startScreen: screen,
          shape: {
            type: "blur",
            block: blurBlockFor(currentSize),
            rect: { x: point.x, y: point.y, w: 0, h: 0 },
          },
        };
        break;
      case "text":
        draftRef.current = { kind: "text-tap", startScreen: screen, at: point };
        break;
      case "crop": {
        const crop = currentDoc.crop;
        const cropOnScreen = toScreenRect(crop, view);
        const corner = hitCorner(screen, cropOnScreen);
        if (corner) {
          draftRef.current = {
            kind: "crop",
            mode: "resize",
            corner,
            rect: crop,
            startRect: crop,
            startImage: imagePoint,
          };
        } else if (isInside(screen, cropOnScreen)) {
          draftRef.current = {
            kind: "crop",
            mode: "move",
            rect: crop,
            startRect: crop,
            startImage: imagePoint,
          };
        } else {
          draftRef.current = {
            kind: "crop",
            mode: "new",
            rect: { x: point.x, y: point.y, w: 0, h: 0 },
            startRect: crop,
            startImage: point,
          };
        }
        break;
      }
    }
    scheduleDraw();
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!pointersRef.current.has(event.pointerId)) {
      return;
    }
    const screen = stagePoint(event);
    pointersRef.current.set(event.pointerId, screen);
    if (pinchRef.current) {
      updatePinch();
      return;
    }
    const draft = draftRef.current;
    const view = viewRef.current;
    const { size: currentSize, doc: currentDoc } = latest.current;
    if (!draft || !view || !currentSize || !currentDoc) {
      return;
    }
    const full = fullImageRect(currentSize);
    const bounds = latest.current.cropMode ? full : currentDoc.crop;
    const point = clampPointToRect(toImagePoint(screen, view), bounds);

    if (draft.kind === "shape") {
      const shape = draft.shape;
      if (shape.type === "pen") {
        // Coalesced events keep fast pen / stylus strokes smooth.
        const native = event.nativeEvent as PointerEvent;
        const samples =
          typeof native.getCoalescedEvents === "function"
            ? native.getCoalescedEvents()
            : [];
        const screens = samples.length > 0 ? samples.map(stagePoint) : [screen];
        const minStep = 1.5 / view.scale;
        const points = [...shape.points];
        screens.forEach((sample) => {
          const next = clampPointToRect(toImagePoint(sample, view), bounds);
          const last = points[points.length - 1];
          if (!last || distance(last, next) >= minStep) {
            points.push(next);
          }
        });
        draft.shape = { ...shape, points };
      } else if (shape.type === "arrow") {
        draft.shape = { ...shape, to: point };
      } else if (shape.type === "rect" || shape.type === "blur") {
        draft.shape = { ...shape, rect: rectFromPoints(draft.anchor, point) };
      }
    } else if (draft.kind === "crop") {
      const imagePoint = toImagePoint(screen, view);
      if (draft.mode === "new") {
        draft.rect = rectFromPoints(
          draft.startImage,
          clampPointToRect(imagePoint, full),
        );
      } else if (draft.mode === "move") {
        const dx = imagePoint.x - draft.startImage.x;
        const dy = imagePoint.y - draft.startImage.y;
        draft.rect = {
          ...draft.startRect,
          x: clamp(draft.startRect.x + dx, 0, full.w - draft.startRect.w),
          y: clamp(draft.startRect.y + dy, 0, full.h - draft.startRect.h),
        };
      } else {
        const p = clampPointToRect(imagePoint, full);
        const start = draft.startRect;
        let left = start.x;
        let top = start.y;
        let right = start.x + start.w;
        let bottom = start.y + start.h;
        if (draft.corner === "nw" || draft.corner === "sw") {
          left = Math.min(p.x, right - MIN_CROP_PX);
        } else {
          right = Math.max(p.x, left + MIN_CROP_PX);
        }
        if (draft.corner === "nw" || draft.corner === "ne") {
          top = Math.min(p.y, bottom - MIN_CROP_PX);
        } else {
          bottom = Math.max(p.y, top + MIN_CROP_PX);
        }
        draft.rect = { x: left, y: top, w: right - left, h: bottom - top };
      }
    }
    scheduleDraw();
  };

  const finishPointer = (
    event: ReactPointerEvent<HTMLCanvasElement>,
    cancelled: boolean,
  ) => {
    if (!pointersRef.current.has(event.pointerId)) {
      return;
    }
    pointersRef.current.delete(event.pointerId);
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch (_) {}
    if (pinchRef.current) {
      if (pointersRef.current.size < 2) {
        pinchRef.current = null;
      }
      return;
    }
    const draft = draftRef.current;
    draftRef.current = null;
    const view = viewRef.current;
    const { size: currentSize, doc: currentDoc } = latest.current;
    if (!draft || cancelled || !view || !currentSize || !currentDoc) {
      scheduleDraw();
      return;
    }
    if (draft.kind === "text-tap") {
      if (distance(draft.startScreen, stagePoint(event)) < 12) {
        setTextEdit({ at: draft.at, value: "" });
      }
      scheduleDraw();
      return;
    }
    if (draft.kind === "crop") {
      const full = fullImageRect(currentSize);
      const rect = intersectRects(draft.rect, full);
      if (rect && rect.w >= MIN_CROP_PX && rect.h >= MIN_CROP_PX) {
        pushDocument({ ...currentDoc, crop: rect });
      }
      scheduleDraw();
      return;
    }
    const shape = draft.shape;
    const onScreen = (value: number) => value * view.scale;
    const isUsable =
      shape.type === "pen" ||
      (shape.type === "arrow" &&
        onScreen(distance(shape.from, shape.to)) >= 10) ||
      ((shape.type === "rect" || shape.type === "blur") &&
        onScreen(shape.rect.w) >= 6 &&
        onScreen(shape.rect.h) >= 6);
    if (isUsable) {
      pushDocument({ ...currentDoc, shapes: [...currentDoc.shapes, shape] });
    }
    scheduleDraw();
  };

  // ── Wheel: pinch-zoom on trackpads (ctrl / ⌘ + wheel) and pan when zoomed ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const view = viewRef.current;
      const rect = focusRect();
      const stage = latest.current.stageSize;
      if (!view || !rect) {
        return;
      }
      const fit = fitView(rect, stage);
      if (event.ctrlKey || event.metaKey) {
        const point = stagePoint(event);
        const scale = clamp(
          view.scale * Math.exp(-event.deltaY * 0.01),
          fit.scale,
          fit.scale * MAX_ZOOM,
        );
        const anchor = toImagePoint(point, view);
        viewRef.current =
          scale <= fit.scale * 1.01
            ? fit
            : clampView(
                {
                  scale,
                  x: point.x - anchor.x * scale,
                  y: point.y - anchor.y * scale,
                },
                rect,
                stage,
              );
      } else if (view.scale > fit.scale * 1.01) {
        viewRef.current = clampView(
          {
            scale: view.scale,
            x: view.x - event.deltaX,
            y: view.y - event.deltaY,
          },
          rect,
          stage,
        );
      }
      scheduleDraw();
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Keyboard: Esc cancels (never closes the widget), undo / redo, focus trap ──
  const keyActions = useRef({ undo, redo });
  keyActions.current = { undo, redo };
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (colorsOpenRef.current) {
          setColorsOpen(false);
          return;
        }
        if (textEditRef.current) {
          setTextEdit(null);
          return;
        }
        // Esc on "Discard your markup?" keeps editing.
        if (latest.current.confirmDiscard) {
          setConfirmDiscard(false);
          return;
        }
        // Esc leaves crop mode (keeping the crop already applied) instead of
        // throwing away the whole screenshot and its markup.
        if (latest.current.cropMode && !latest.current.isBusy) {
          if (draftRef.current?.kind === "crop") {
            draftRef.current = null;
          }
          setTool(lastMarkToolRef.current);
          return;
        }
        if (!latest.current.isBusy) {
          latest.current.onCancel();
        }
        return;
      }
      const root = rootRef.current;
      if (event.key === "Tab" && root) {
        const focusable = Array.from(
          root.querySelectorAll<HTMLElement>(
            "button:not([disabled]), input:not([disabled]), [tabindex='0']",
          ),
        );
        if (focusable.length === 0) {
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && (active === first || !root.contains(active))) {
          event.preventDefault();
          last.focus();
        } else if (
          !event.shiftKey &&
          (active === last || !root.contains(active))
        ) {
          event.preventDefault();
          first.focus();
        }
        return;
      }
      const isTyping = document.activeElement === textInputRef.current;
      const key = event.key.toLowerCase();
      if (!isTyping && (event.metaKey || event.ctrlKey) && key === "z") {
        event.preventDefault();
        if (event.shiftKey) {
          keyActions.current.redo();
        } else {
          keyActions.current.undo();
        }
      } else if (!isTyping && (event.metaKey || event.ctrlKey) && key === "y") {
        event.preventDefault();
        keyActions.current.redo();
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, []);

  // Focus moves into the dialog and back to where it was on close.
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    rootRef.current?.focus({ preventScroll: true });
    return () => {
      try {
        previous?.focus?.({ preventScroll: true });
      } catch (_) {}
    };
  }, []);

  // A new text field: place it before paint, then focus it (within the tap's
  // event handling, so mobile keyboards open).
  const isEditingText = !!textEdit;
  useLayoutEffect(() => {
    if (!isEditingText) {
      return;
    }
    if (viewRef.current) {
      positionTextInput(viewRef.current);
    }
    textInputRef.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingText]);

  // ── Send ──
  const send = async () => {
    if (!image || !size || !doc || isBusy) {
      return;
    }
    let finalDoc = doc;
    const pendingText = textEditRef.current;
    if (pendingText) {
      const shape = textShapeFor(pendingText);
      setTextEdit(null);
      if (shape) {
        finalDoc = { ...doc, shapes: [...doc.shapes, shape] };
        pushDocument(finalDoc);
      }
    }
    setExporting(true);
    setExportFailed(false);
    try {
      const result = await exportAnnotatedCapture(
        image,
        size,
        annotate ? finalDoc : { shapes: [], crop: fullImageRect(size) },
      );
      setExporting(false);
      onSend(result);
    } catch (_) {
      setExporting(false);
      setExportFailed(true);
    }
  };

  const titleId = "capture-editor-title";
  const progressPercent =
    typeof progress === "number" ? Math.round(progress * 100) : undefined;
  const showError = (hasError || exportFailed || imageFailed) && !isBusy;
  // Position / scale are set imperatively with every redraw (they follow
  // pinch-zoom without re-rendering); React owns only the look.
  const textStyle = textEdit
    ? {
        color,
        font: textFont(16),
        lineHeight: TEXT_LINE_HEIGHT,
        width: `${Math.max(3, (textEdit.value.length || 1) + 2)}ch`,
      }
    : undefined;

  return (
    <div
      ref={rootRef}
      className={[
        "capture-editor",
        annotate ? "" : "capture-editor--preview",
        isBusy ? "capture-editor--busy" : "",
        layout.wide ? "capture-editor--wide" : "",
        layout.short ? "capture-editor--short" : "",
        layout.compact ? "capture-editor--compact" : "",
        layout.stacked ? "capture-editor--stacked" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      data-capture-editor-ready={image && history ? "true" : "false"}
    >
      <div className="capture-editor-header">
        <div className="capture-editor-heading">
          <h2 id={titleId} className="capture-editor-title">
            {t("captureEditorTitle")}
          </h2>
          {annotate && (
            <span className="capture-editor-subtitle">
              {t("captureEditorHint")}
            </span>
          )}
        </div>
        <button
          type="button"
          className="capture-editor-close"
          aria-label={t("captureBarCancel")}
          title={t("captureBarCancel")}
          disabled={isBusy}
          onClick={requestCancel}
        >
          <CloseIcon />
        </button>
      </div>

      <div
        ref={stageRef}
        className={`capture-editor-stage capture-editor-stage--${annotate ? tool : "preview"}`}
      >
        <canvas
          ref={canvasRef}
          className="capture-editor-canvas"
          aria-hidden="true"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={(event) => finishPointer(event, false)}
          onPointerCancel={(event) => finishPointer(event, true)}
          onContextMenu={(event) => event.preventDefault()}
        />
        {textEdit && textStyle && (
          <input
            ref={textInputRef}
            className="capture-editor-text-input"
            style={textStyle}
            autoFocus
            value={textEdit.value}
            placeholder={t("captureEditorText")}
            aria-label={t("captureEditorText")}
            maxLength={200}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setTextEdit({ at: textEdit.at, value: event.target.value })
            }
            onKeyDown={(event: ReactKeyboardEvent<HTMLInputElement>) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitText();
              }
            }}
            onBlur={commitText}
          />
        )}
        {sending && (
          <div className="capture-editor-progress" aria-hidden="true">
            <span style={{ width: `${progressPercent ?? 0}%` }} />
          </div>
        )}
        {confirmDiscard && !isBusy && (
          <div
            className="capture-editor-confirm"
            role="alertdialog"
            aria-label={t("captureEditorDiscardQuestion")}
          >
            <span>{t("captureEditorDiscardQuestion")}</span>
            <button
              type="button"
              className="capture-editor-confirm-keep"
              autoFocus
              onClick={() => setConfirmDiscard(false)}
            >
              {t("captureEditorKeepEditing")}
            </button>
            <button
              type="button"
              className="capture-editor-confirm-discard"
              onClick={onCancel}
            >
              {t("capturePreviewDiscard")}
            </button>
          </div>
        )}
        {showError && (
          <div className="capture-editor-error" role="alert">
            <AlertIcon />
            <span>{t("captureFailed")}</span>
          </div>
        )}
      </div>

      <div className="capture-editor-toolbar">
        <div
          ref={barRef}
          className="capture-editor-bar"
          role="toolbar"
          aria-label={t("captureEditorTitle")}
        >
          {annotate && (
            <div className="capture-editor-group capture-editor-group--tools">
              {TOOLS.map(({ tool: option, label, Icon }) => (
                <button
                  key={option}
                  type="button"
                  className="capture-editor-tool"
                  aria-pressed={tool === option}
                  aria-label={t(label)}
                  title={t(label)}
                  disabled={isBusy}
                  onClick={() => selectTool(option)}
                >
                  <Icon />
                  <span className="capture-editor-tool-label">{t(label)}</span>
                </button>
              ))}
              <div className="capture-editor-color">
                <button
                  type="button"
                  className="capture-editor-tool capture-editor-tool--icon capture-editor-color-button"
                  aria-label={t("captureEditorColor")}
                  title={t("captureEditorColor")}
                  aria-haspopup="true"
                  aria-expanded={colorsOpen}
                  disabled={isBusy || tool === "blur" || tool === "crop"}
                  style={{ ["--swatch" as any]: color }}
                  onClick={() => setColorsOpen((open) => !open)}
                >
                  <span className="capture-editor-color-dot" />
                </button>
                {colorsOpen && (
                  <div
                    className="capture-editor-colors"
                    role="radiogroup"
                    aria-label={t("captureEditorColor")}
                  >
                    {CAPTURE_EDITOR_COLORS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        role="radio"
                        className="capture-editor-swatch"
                        aria-checked={color === option.value}
                        aria-label={option.name}
                        title={option.name}
                        style={{ ["--swatch" as any]: option.value }}
                        onClick={() => {
                          setColor(option.value);
                          setColorsOpen(false);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          {annotate && (
            <span className="capture-editor-sep" aria-hidden="true" />
          )}
          <div className="capture-editor-group capture-editor-group--actions">
            {annotate && (
              <>
                <button
                  type="button"
                  className="capture-editor-tool capture-editor-tool--icon"
                  aria-label={t("captureEditorUndo")}
                  title={t("captureEditorUndo")}
                  disabled={isBusy || !canUndo}
                  onClick={undo}
                >
                  <UndoIcon />
                </button>
                <button
                  type="button"
                  className="capture-editor-tool capture-editor-tool--icon"
                  aria-label={t("captureEditorRedo")}
                  title={t("captureEditorRedo")}
                  disabled={isBusy || !canRedo}
                  onClick={redo}
                >
                  <RedoIcon />
                </button>
              </>
            )}
            {canRetake && (
              <button
                type="button"
                className="capture-editor-tool"
                aria-label={t("captureEditorRetake")}
                title={t("captureEditorRetake")}
                disabled={isBusy}
                onClick={onRetake}
              >
                <RetakeIcon />
                <span className="capture-editor-tool-label">
                  {t("captureEditorRetake")}
                </span>
              </button>
            )}
            <button
              type="button"
              className="capture-editor-send"
              disabled={isBusy || !image}
              aria-busy={isBusy || undefined}
              onClick={send}
            >
              {isBusy ? (
                <>
                  <span className="capture-editor-spinner" aria-hidden="true" />
                  <span>
                    {sending && progressPercent !== undefined
                      ? `${progressPercent}%`
                      : t("captureUploading")}
                  </span>
                </>
              ) : (
                <>
                  <span>{t("captureEditorSend")}</span>
                  <ArrowUpIcon />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
